import { motion } from 'framer-motion';
import { useNavigation } from '../context/NavigationContext';
import { useEffect, useState, useRef } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { fetchCaseStudy, fetchNextCaseStudy } from '../lib/sanity';
import { PortableText } from '@portabletext/react';
import YouTubeEmbed from '../components/YouTubeEmbed';
import Carousel from '../components/Carousel';
import Grid from '../components/Grid';
import Lottie from 'lottie-react';
import playAnimation from '../assets/lottie_animations/play.json';
import playHoverAnimation from '../assets/lottie_animations/play hover.json';
import playHoverOutAnimation from '../assets/lottie_animations/play hover out.json';
import playOutAnimation from '../assets/lottie_animations/play out.json';
import './CaseStudy.css';
import React from 'react';
import SiteMeta from '../components/SiteMeta';

// Create a context for case study colors
export const CaseStudyColorContext = React.createContext({
  background: '#ffffff',
  font: '#000000',
  accent: '#ff0000'
});

// Define custom components for Portable Text
const components = {
  block: {
    h1: ({children}) => <h1 className="portable-text-h1">{children}</h1>,
    h2: ({children}) => <h2 className="portable-text-h2">{children}</h2>,
    h3: ({children}) => <h3 className="portable-text-h3">{children}</h3>,
    normal: ({children}) => <p className="portable-text-p">{children}</p>,
  },
  marks: {
    strong: ({children}) => <strong className="portable-text-strong">{children}</strong>,
    em: ({children}) => <em className="portable-text-em">{children}</em>,
    code: ({children}) => <code className="portable-text-code">{children}</code>,
    link: ({value, children}) => {
      const target = (value?.href || '').startsWith('http') ? '_blank' : undefined
      return (
        <a
          href={value?.href}
          target={target}
          rel={target === '_blank' ? 'noindex nofollow' : undefined}
        >
          {children}
        </a>
      )
    },
  },
  list: {
    bullet: ({children}) => <ul className="portable-text-ul">{children}</ul>,
    number: ({children}) => <ol className="portable-text-ol">{children}</ol>,
  },
  listItem: {
    bullet: ({children}) => <li className="portable-text-li">{children}</li>,
    number: ({children}) => <li className="portable-text-li">{children}</li>,
  },
};

function VideoModule({ module }) {
  const [isPlaying, setIsPlaying] = useState(module.autoplay);
  const [isHovered, setIsHovered] = useState(false);
  const [hasPlayedOnce, setHasPlayedOnce] = useState(false);
  const [hasClicked, setHasClicked] = useState(false);
  const videoRef = useRef(null);
  const { useContext } = React;
  const { background, font } = useContext(CaseStudyColorContext);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          if (module.autoplay) {
            video.play().catch(err => {
              console.error('Autoplay prevented:', err);
            });
          }
        } else {
          video.pause();
        }
      },
      { threshold: 0.1 }
    );

    observer.observe(video);

    return () => {
      observer.disconnect();
    };
  }, [module.autoplay]);

  const togglePlayPause = () => {
    if (videoRef.current.paused) {
      videoRef.current.play();
      setIsPlaying(true);
      setHasClicked(true);
    } else {
      videoRef.current.pause();
      setIsPlaying(false);
    }
  };

  const handleMouseEnter = () => {
    setIsHovered(true);
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
  };

  const handleLottieComplete = () => {
    if (!hasPlayedOnce) {
      setHasPlayedOnce(true);
    }
  };

  // Determine which animation to show for non-autoplay videos
  const getLottieAnimation = () => {
    // Only show Lottie for videos that are not in autoplay
    if (module.autoplay) return null;

    // If clicked, show play out animation
    if (hasClicked) {
      return playOutAnimation;
    }

    // If animation has played once, check hover state
    if (hasPlayedOnce) {
      if (isHovered) {
        return playHoverAnimation;
      } else {
        return playHoverOutAnimation;
      }
    }

    // Default: show play animation when video is paused
    if (!isPlaying) {
      return playAnimation;
    }

    return playAnimation;
  };

  const currentAnimation = getLottieAnimation();

  return (
    <div 
      className="module video-module"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onClick={togglePlayPause}
      style={{ position: 'relative', cursor: 'pointer' }}
    >
      <video 
        ref={videoRef}
        src={module.video.asset.url}
        poster={module.poster?.asset.url}
        playsInline
        loop={module.autoplay}
        muted={module.autoplay}
        onPlay={() => setIsPlaying(true)}
        onPause={() => setIsPlaying(false)}
        onEnded={() => {
          setIsPlaying(false);
          setHasClicked(false);
        }}
      />
      
      {/* Lottie Animation Overlay for non-autoplay videos */}
      {currentAnimation && (
        <div 
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            pointerEvents: 'none',
            zIndex: 10
          }}
        >
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            height: '100%'
          }}>
            <Lottie
              animationData={currentAnimation}
              style={{
                width: '100%',
                height: 'auto'
              }}
              loop={false}
              autoplay={true}
              onComplete={handleLottieComplete}
            />
          </div>
        </div>
      )}
      
      {/* Gradient Overlay - Uses case study background color */}
      <div 
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `linear-gradient(to bottom, ${background}00 0%, ${background}00 70%, ${background}99 98%)`,
          opacity: isHovered ? 1 : 0,
          transition: 'opacity 0.3s ease',
          pointerEvents: 'none',
          zIndex: 1
        }}
      />
      
      {isHovered && (
        <div 
          style={{
            position: 'absolute',
            bottom: '1rem',
            right: '1rem',
            padding: '0.5rem 1rem',
            color: font,
            borderRadius: '4px',
            fontSize: '1.5rem',
            fontFamily: 'leShka_Sans_light, sans-serif',
            transition: 'opacity 0.2s ease',
            opacity: isHovered ? 1 : 0,
            pointerEvents: 'none',
            zIndex: 2
          }}
        >
          {isPlaying ? 'Pause' : 'Play'}
        </div>
      )}
    </div>
  );
}

function SmallVideoModule({ module }) {
  const [isPlaying, setIsPlaying] = useState(module.autoplay);
  const [isHovered, setIsHovered] = useState(false);
  const [hasPlayedOnce, setHasPlayedOnce] = useState(false);
  const [hasClicked, setHasClicked] = useState(false);
  const videoRef = useRef(null);
  const { useContext } = React;
  // eslint-disable-next-line no-unused-vars
  const { background, font } = useContext(CaseStudyColorContext);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          if (module.autoplay) {
            video.play().catch(err => {
              console.error('Autoplay prevented:', err);
            });
          }
        } else {
          video.pause();
        }
      },
      { threshold: 0.1 }
    );

    observer.observe(video);

    return () => {
      observer.disconnect();
    };
  }, [module.autoplay]);

  const togglePlayPause = () => {
    if (videoRef.current.paused) {
      videoRef.current.play();
      setIsPlaying(true);
      setHasClicked(true);
    } else {
      videoRef.current.pause();
      setIsPlaying(false);
    }
  };

  const handleMouseEnter = () => {
    setIsHovered(true);
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
  };

  const handleLottieComplete = () => {
    if (!hasPlayedOnce) {
      setHasPlayedOnce(true);
    }
  };

  // Determine which animation to show for non-autoplay videos
  const getLottieAnimation = () => {
    // Only show Lottie for videos that are not in autoplay
    if (module.autoplay) return null;

    // If clicked, show play out animation
    if (hasClicked) {
      return playOutAnimation;
    }

    // If animation has played once, check hover state
    if (hasPlayedOnce) {
      if (isHovered) {
        return playHoverAnimation;
      } else {
        return playHoverOutAnimation;
      }
    }

    // Default: show play animation when video is paused
    if (!isPlaying) {
      return playAnimation;
    }

    return playAnimation;
  };

  const currentAnimation = getLottieAnimation();

  return (
    <div 
      className="module small-image-module"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onClick={togglePlayPause}
      style={{ position: 'relative', cursor: 'pointer' }}
    >
      <video 
        ref={videoRef}
        src={module.video.asset.url}
        poster={module.poster?.asset?.url}
        aria-label={module.alt}
        playsInline
        loop={module.autoplay}
        muted={module.autoplay}
        onPlay={() => setIsPlaying(true)}
        onPause={() => setIsPlaying(false)}
        onEnded={() => {
          setIsPlaying(false);
          setHasClicked(false);
        }}
      />
      
      {/* Lottie Animation Overlay for non-autoplay videos */}
      {currentAnimation && (
        <div 
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            pointerEvents: 'none',
            zIndex: 10
          }}
        >
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '80%',
            height: 'auto',
            maxWidth: '720px'
          }}>
            <Lottie
              animationData={currentAnimation}
              style={{
                width: '100%',
                height: 'auto'
              }}
              loop={false}
              autoplay={true}
              onComplete={handleLottieComplete}
            />
          </div>
        </div>
      )}
    </div>
  );
}

function CaseStudy() {
  const { slug } = useParams();
  const location = useLocation();
  const { destinationPage, activePage, clearDestination } = useNavigation();
  const isDestination = destinationPage === 'case-study';
  const isActive = activePage === 'case-study';
  const [, setPreviousPage] = useState(location.state?.from || 'work');
  const [caseStudy, setCaseStudy] = useState(null);
  const [nextCaseStudy, setNextCaseStudy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isHeaderTransitioned, setIsHeaderTransitioned] = useState(false);
  const [isNextExpanded, setIsNextExpanded] = useState(false);
  const [isTitleTransitioning, setIsTitleTransitioning] = useState(false);
  const [currentColors, setCurrentColors] = useState({
    background: '#ffffff',
    font: '#000000',
    accent: '#ff0000'
  });
  const heroRef = useRef(null);

  useEffect(() => {
    // Store the previous page when component mounts
    if (location.state?.from) {
      setPreviousPage(location.state.from);
    }
  }, [location]);

  // Update page title when case study changes
  useEffect(() => {
    if (caseStudy) {
      let title = caseStudy.titleLineOne || 'Case Study';
      if (caseStudy.titleLineTwo && caseStudy.titleLineTwo.trim() !== '') {
        title = `${title} ${caseStudy.titleLineTwo}`;
      }
      document.title = `${title} - Robin Gardeur (le Shka)`;
    }
  }, [caseStudy]);

  // Add effect to update body colors
  useEffect(() => {
    // Apply colors to body
    document.body.style.backgroundColor = currentColors.background;
    document.body.style.color = currentColors.font;
    document.body.style.transition = 'background-color 1s cubic-bezier(0.4, 0, 0.2, 1), color 1s cubic-bezier(0.4, 0, 0.2, 1)';
    
    // Update CSS custom properties for nav buttons
    document.documentElement.style.setProperty('--nav-button-bg', currentColors.background);
    document.documentElement.style.setProperty('--nav-button-color', currentColors.font);
    document.documentElement.style.setProperty('--nav-button-border', currentColors.font);
    document.documentElement.style.setProperty('--nav-button-hover-bg', currentColors.font);
    document.documentElement.style.setProperty('--nav-button-hover-color', currentColors.background);
    document.documentElement.style.setProperty('--nav-button-active-bg', currentColors.font);
    document.documentElement.style.setProperty('--nav-button-active-color', currentColors.background);
    
    // Set CSS custom property for case study background color (for tag hover effect)
    document.documentElement.style.setProperty('--case-study-background', currentColors.background);
    document.documentElement.style.setProperty('--case-study-font', currentColors.font);
    
    // Set CSS custom property for tag background (5% opacity of font color)
    const fontColor = currentColors.font;
    const tagBgColor = fontColor.startsWith('#') 
      ? `${fontColor}0D` // Add 0D for 5% opacity in hex
      : `rgba(${fontColor.replace(/[^\d,]/g, '').split(',').map(c => c.trim()).join(', ')}, 0.05)`;
    document.documentElement.style.setProperty('--case-study-tag-bg', tagBgColor);
    
    // Cleanup function to reset styles when component unmounts
    return () => {
      document.body.style.backgroundColor = '';
      document.body.style.color = '';
      document.body.style.transition = '';
      // Reset CSS custom properties
      document.documentElement.style.removeProperty('--nav-button-bg');
      document.documentElement.style.removeProperty('--nav-button-color');
      document.documentElement.style.removeProperty('--nav-button-border');
      document.documentElement.style.removeProperty('--nav-button-hover-bg');
      document.documentElement.style.removeProperty('--nav-button-hover-color');
      document.documentElement.style.removeProperty('--nav-button-active-bg');
      document.documentElement.style.removeProperty('--nav-button-active-color');
      document.documentElement.style.removeProperty('--case-study-background');
      document.documentElement.style.removeProperty('--case-study-font');
    };
  }, [currentColors.background, currentColors.font]);

  useEffect(() => {
    const loadContent = async () => {
      try {
        setLoading(true);
        setIsHeaderTransitioned(false);
        setIsNextExpanded(false);
        const [currentStudy, nextStudy] = await Promise.all([
          fetchCaseStudy(slug),
          fetchNextCaseStudy(slug)
        ]);
        setCaseStudy(currentStudy);
        setNextCaseStudy(nextStudy);
        setCurrentColors({
          background: currentStudy.colorScheme?.background || '#ffffff',
          font: currentStudy.colorScheme?.font || '#000000',
          accent: currentStudy.colorScheme?.accent || '#ff0000'
        });
        window.scrollTo(0, 0);

        // Start header transition after 1 second
        const timer = setTimeout(() => {
          setIsHeaderTransitioned(true);
        }, 1000);

        return () => clearTimeout(timer);
      } catch (err) {
        console.error('Error fetching case study:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    loadContent();
  }, [slug]);

  const handleNextProjectClick = async () => {
    if (nextCaseStudy) {
      // Start color transition
      setCurrentColors({
        background: nextCaseStudy.colorScheme?.background || '#ffffff',
        font: nextCaseStudy.colorScheme?.font || '#000000',
        accent: nextCaseStudy.colorScheme?.accent || '#ff0000'
      });

      // Start title transition
      setIsTitleTransitioning(true);

      // Expand the next project section
      setIsNextExpanded(true);
      
      // Smooth scroll to the footer
      const footer = document.querySelector('.next-project-preview');
      const startPosition = window.pageYOffset;
      const targetPosition = footer.offsetTop;
      const distance = targetPosition - startPosition;
      const startTime = performance.now();
      const duration = 1010; // 1 second
      
      const easeInOutCubic = (t) => {
        return t < 0.5
          ? 4 * t * t * t
          : 1 - Math.pow(-2 * t + 2, 3) / 2;
      };

      const animateScroll = (currentTime) => {
        const elapsed = currentTime - startTime;
        const progress = Math.min(elapsed / duration, 1);
        
        // animation scroll
        const downProgress = progress * 2; // Scale to 0-1
        const easedDownProgress = easeInOutCubic(downProgress);
        window.scrollTo(0, startPosition + distance * easedDownProgress);
        
        if (progress < 1) {
          requestAnimationFrame(animateScroll);
        } else {
          window.scrollTo(0, 0);
        }
      };
      
      requestAnimationFrame(animateScroll);
      
      // After the expansion animation, update the content and URL
      setTimeout(async () => {
        // Reset header height
        setIsHeaderTransitioned(false);
        
        // Update the current case study
        setCaseStudy(nextCaseStudy);
        
        // Fetch the next-next case study
        const nextNext = await fetchNextCaseStudy(nextCaseStudy.slug.current);
        setNextCaseStudy(nextNext);

        // Update URL and page title
        const newUrl = `/case-study/${nextCaseStudy.slug.current}`;
        window.history.pushState({}, '', newUrl);
        
        // Update page title
        let title = nextCaseStudy.titleLineOne || 'Case Study';
        if (nextCaseStudy.titleLineTwo && nextCaseStudy.titleLineTwo.trim() !== '') {
          title = `${title} ${nextCaseStudy.titleLineTwo}`;
        }
        document.title = `${title} - Robin Gardeur (le Shka)`;

        // Start the header transition after a short delay
        setTimeout(() => {
          setIsHeaderTransitioned(true);
          setIsNextExpanded(false);
          // End title transition after the animation
          setIsTitleTransitioning(false);
        }, 100);
      }, 1000); // Wait for the expansion animation to complete
    }
  };

  if (loading && !caseStudy) return <div className="page">Loading...</div>;
  if (error) return <div className="page">Error: {error}</div>;
  if (!caseStudy) return <div className="page">Case study not found</div>;

  return (
    <CaseStudyColorContext.Provider value={currentColors}>
      <SiteMeta
        title={`${caseStudy.titleLineOne || 'Case Study'} ${caseStudy.titleLineTwo || ''} - Robin Gardeur (le Shka)`}
        description={`Case study: ${caseStudy.titleLineOne || ''} ${caseStudy.titleLineTwo || ''} by Robin Gardeur (le Shka)`}
        image={caseStudy.heroImage?.desktop?.asset?.url || caseStudy.thumbnail}
      />
      <motion.div
        className={`page case-study-page ${isDestination ? 'page-transitioning' : ''} ${isActive ? 'page-active' : ''}`}
        initial={{ y: '100vh' }}
        animate={{ y: 0 }}
        exit={{ y: '-20vh' }}
        transition={{ 
          duration: 1,
          ease: [0.4, 0, 0.2, 1],
          type: "tween"
        }}
        onAnimationStart={(definition) => {
          if (definition === 'exit') {
            const element = document.querySelector('.case-study-page');
            if (element) element.classList.add('page-exiting');
          }
        }}
        onAnimationComplete={() => {
          if (isDestination) {
            clearDestination();
          }
        }}
        style={{
          backgroundColor: currentColors.background,
          color: currentColors.font
        }}
      >
        {/* Hero Section */}
        <section 
          ref={heroRef}
          className={`case-study-hero ${isHeaderTransitioned ? 'transitioned' : ''}`}
        >
          <div className="hero-content">
            <h2 className={`next-project-label ${isTitleTransitioning ? 'transitioning' : ''}`}>{caseStudy.year}</h2>
            <div className="title-container">
              <h1 style={{ color: currentColors.accent }}>
                {caseStudy.titleLineOne || 'No Title Line One'}
                {caseStudy.titleLineTwo && (
                  <>
                    <br />
                    {caseStudy.titleLineTwo}
                  </>
                )}
              </h1>
            </div>
              <div className="hero-meta">
              <div className="tags">
                {caseStudy.tags?.map((tag, index) => (
                  <span key={index} className="tag">{tag.replace(/-/g, '\u2011')}</span>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Hero Image */}
        {caseStudy.heroImage?.desktop?.asset?.url && (
          <div className="hero-image-module">
            <div className="hero-image-container">
              <img 
                src={caseStudy.heroImage.desktop.asset.url} 
                alt={caseStudy.heroImage.alt || caseStudy.title}
              />
            </div>
          </div>
        )}

        {/* Content Modules - Full Width */}
        <section className="case-study-content">
          {caseStudy.content?.map((module, index) => {
            switch (module._type) {
              case 'imageModule':
                return (
                  <div key={index} className="module image-module">
                    <img 
                      src={module.image.asset.url} 
                      alt={module.alt}
                    />
                  </div>
                );
              
              case 'smallImageModule':
                if (module.type === 'video' && module.video?.asset?.url) {
                  return <SmallVideoModule key={index} module={module} />;
                }
                return (
                  <div key={index} className="module small-image-module">
                    <img 
                      src={module.image?.asset?.url} 
                      alt={module.alt}
                    />
                  </div>
                );
              
              case 'videoModule':
                return <VideoModule key={index} module={module} />;
              
              case 'copyModule':
                return (
                  <div key={index} className={`module copy-module ${module.size}`}>
                    <div className="copy-content">
                      <PortableText value={module.content} components={components} />
                    </div>
                  </div>
                );
              
              case 'imageCopyModule':
                return (
                  <div key={index} className={`module image-copy-module ${module.imagePosition}`}>
                    <div className="image-copy-content">
                      <img 
                        src={module.image.asset.url} 
                        alt={module.alt}
                        // style={{ width: '100%', height: 'auto' }}
                      />
                      <div className="copy-content">
                        <PortableText value={module.content} components={components} />
                      </div>
                    </div>
                  </div>
                );
              
              case 'youtubeModule':
                return <YouTubeEmbed key={index} module={module} />;
              
              case 'carouselModule':
                return <Carousel key={index} module={module} />;
              
              case 'gridModule':
                return <Grid key={index} module={module} />;
              
              default:
                return null;
            }
          })}
        </section>

        {/* Next Project Preview */}
        {nextCaseStudy && (
          <section 
            className={`next-project-preview ${isNextExpanded ? 'expanded' : ''}`}
            onClick={handleNextProjectClick}
            style={{ cursor: 'pointer' }}
          >
            <div className="next-project-content">
              <h2 className={`next-project-label ${isTitleTransitioning ? 'transitioning' : ''}`}>Next Project</h2>
              <div className="title-container">
                <div className="next-project-title-container">
                  <div className={`next-project-title next-project-title-top ${isTitleTransitioning ? 'transitioning' : ''}`}>
                    <h3 
                      className={`next-project-title next-project-title-top ${isTitleTransitioning ? 'transitioning' : ''}`}
                      style={{ color: isNextExpanded ? (nextCaseStudy.colorScheme?.accent || '#ff0000') : currentColors.font }}
                    >
                      {nextCaseStudy.titleLineOne || 'No Title Line One'}
                    </h3>
                  </div>
                  <div className={`next-project-title next-project-title-bottom ${isTitleTransitioning ? 'transitioning' : ''}`}>
                    <h3 
                      className={`next-project-title next-project-title-bottom ${isTitleTransitioning ? 'transitioning' : ''}`}
                      style={{ color: isNextExpanded ? (nextCaseStudy.colorScheme?.accent || '#ff0000') : currentColors.font }}
                    >
                      {nextCaseStudy.titleLineOne || 'No Title Line One'}
                    </h3>
                  </div>
                </div>
                {nextCaseStudy.titleLineTwo && (
                  <div className={`next-project-title-line ${isTitleTransitioning ? 'transitioning' : ''}`}>
                    <h3 
                      className={`next-project-title next-project-title-top ${isTitleTransitioning ? 'transitioning' : ''}`}
                      style={{ color: isNextExpanded ? (nextCaseStudy.colorScheme?.accent || '#ff0000') : currentColors.font }}
                    >
                      {nextCaseStudy.titleLineTwo}
                    </h3>
                    <h3 
                      className={`next-project-title next-project-title-bottom ${isTitleTransitioning ? 'transitioning' : ''}`}
                      style={{ color: isNextExpanded ? (nextCaseStudy.colorScheme?.accent || '#ff0000') : currentColors.font }}
                    >
                      {nextCaseStudy.titleLineTwo}
                    </h3>
                  </div>
                )}
              </div>
              <div className="next-project-meta">
                <div className="tags">
                  {nextCaseStudy.tags?.map((tag, index) => (
                    <span key={index} className="tag">{tag.replace(/-/g, '\u2011')}</span>
                  ))}
                </div>
              </div>
            </div>
          </section>
        )}
      </motion.div>
    </CaseStudyColorContext.Provider>
  );
}

export default CaseStudy; 